import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Check } from "lucide-react";
import { Logo } from "../components/Logo";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";

const STAGES = [
  "Analyse de votre présence digitale…",
  "Évaluation de votre visibilité locale…",
  "Mesure de votre fidélisation client…",
  "Étude de votre réputation en ligne…",
  "Calcul de votre potentiel commercial…",
  "Génération de vos recommandations…",
];

export default function Analyzing() {
  const nav = useNavigate();
  const loc = useLocation();
  const { setData, user } = useApp();
  const [stage, setStage] = useState(0);
  const done = useRef(false);
  const onboarding = loc.state?.onboarding;

  useEffect(() => {
    if (!onboarding) { nav("/onboarding"); return; }
    const interval = setInterval(() => setStage((s) => Math.min(s + 1, STAGES.length - 1)), 850);
    (async () => {
      let profile;
      try {
        if (user) {
          const { data } = await api.post("/onboarding", onboarding);
          profile = data;
        } else {
          const { data } = await api.post("/analyze", onboarding);
          profile = { onboarding, ...data };
        }
      } catch {
        const { data } = await api.post("/analyze", onboarding);
        profile = { onboarding, ...data };
      }
      setData((prev) => ({ ...(prev || {}), ...profile }));
    })();
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (stage === STAGES.length - 1 && !done.current) {
      done.current = true;
      setTimeout(() => nav("/app/dashboard", { state: { reveal: true } }), 1400);
    }
  }, [stage, nav]);

  return (
    <div className="min-h-screen relative overflow-hidden grain flex items-center justify-center px-5">
      <div className="absolute inset-0 aurora" />
      <div className="relative z-10 max-w-md w-full text-center">
        <div className="flex justify-center mb-10"><Logo size={44} /></div>
        <motion.div className="relative w-28 h-28 mx-auto mb-10" animate={{ rotate: 360 }} transition={{ duration: 3, repeat: Infinity, ease: "linear" }}>
          <div className="absolute inset-0 rounded-full border-2 border-primary/30" />
          <div className="absolute inset-0 rounded-full border-t-2 border-neon" style={{ filter: "drop-shadow(0 0 8px #00E5FF)" }} />
          <div className="absolute inset-4 rounded-full bg-primary/10 flex items-center justify-center">
            <Loader2 className="animate-spin text-neon" size={28} />
          </div>
        </motion.div>
        <h1 className="font-display font-bold text-2xl mb-8">Analyse de votre entreprise en cours…</h1>
        <div className="space-y-3 text-left">
          {STAGES.map((s, i) => (
            <AnimatePresence key={i}>
              {i <= stage && (
                <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                  className="flex items-center gap-3 text-sm">
                  {i < stage ? <Check size={16} className="text-emerald-400" /> : <Loader2 size={16} className="animate-spin text-neon" />}
                  <span className={i < stage ? "text-[hsl(var(--muted-foreground))]" : "text-white"}>{s}</span>
                </motion.div>
              )}
            </AnimatePresence>
          ))}
        </div>
      </div>
    </div>
  );
}
